import { gqlErrorCode, gqlErrorData } from '@/lib/gql_error'
import type { StatusVariant } from '@/lib/status_colors'
import { deliveryStateText } from './signature-model'

/*
 * Display and input rules for Agency CRM proposals (ENT-565 ZDP-T37, R2).
 *
 * The server owns every fact about a proposal: the state, the round, the "already live" decision.
 * This module only (1) turns the server's state names into words, (2) mirrors the create form's
 * input checks so a typo is caught before the round-trip (the server re-checks every one), and
 * (3) maps the server's error names to text. No price or total is computed here.
 */

/** The eleven proposal states of crm-svc (`CRM_PROPOSAL_STATES`), in lifecycle order. */
export const PROPOSAL_STATES = [
  'AWAITING_INPUT',
  'INPUT_IN_PROGRESS',
  'INPUT_SUBMITTED',
  'RENDERED',
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
  'WITHDRAWN',
  'ACCEPTED',
  'EXPIRED',
  'CANCELLED',
] as const
export type ProposalState = (typeof PROPOSAL_STATES)[number]

export const PROPOSAL_STATE_LABEL: Record<ProposalState, string> = {
  AWAITING_INPUT: 'Awaiting input',
  INPUT_IN_PROGRESS: 'Input in progress',
  INPUT_SUBMITTED: 'Input submitted',
  RENDERED: 'Rendered',
  PENDING_APPROVAL: 'Pending approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
  ACCEPTED: 'Accepted',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
}

/** The state's words; an unknown server state is shown as it came, never hidden. */
export function proposalStateLabel(state: string): string {
  return (PROPOSAL_STATE_LABEL as Record<string, string>)[state] ?? state
}

/**
 * The headline status of a proposal: a DISPLAY status derived from the stored lifecycle state and the latest
 * signature delivery (the data the Signing panel already loads). The stored state is never changed and is
 * still what every rule and action reads; this only decides which words and colour the chip shows.
 *
 * Only an APPROVED proposal is ever re-worded, because only an approved proposal can be sent for signature:
 *   - a signature request being made or waiting for the client -> "Awaiting client signature";
 *   - the client has signed (the acceptance is still being recorded) -> "Signed";
 *   - the request ended without a signature (declined, expired, failed, not confirmed, withdrawn) -> the
 *     lifecycle word with the delivery's own label from signature-model: "Approved \u00b7 Declined";
 *   - anything else (no delivery, a replaced delivery, a state this build does not know) -> unchanged.
 */
export interface ProposalDisplayStatus {
  /** The state to resolve the chip's dot/badge shape from: always the stored lifecycle state. */
  status: string
  label: string
  /** Colour override; absent when the chip keeps the lifecycle state's own colour. */
  variant?: StatusVariant
}

const AWAITING_CLIENT_STATES: readonly string[] = ['REQUESTING_SIGNATURE', 'AWAITING_SIGNATURE']
const ENDED_UNSIGNED_STATES: readonly string[] = ['DECLINED', 'EXPIRED', 'REQUEST_FAILED', 'REQUEST_UNCONFIRMED', 'VOIDED']

export const AWAITING_CLIENT_SIGNATURE_LABEL = 'Awaiting client signature'

export function proposalDisplayStatus(state: string, deliveryState?: string | null): ProposalDisplayStatus {
  const plain: ProposalDisplayStatus = { status: state, label: proposalStateLabel(state) }
  if (state !== 'APPROVED' || !deliveryState) return plain
  if (AWAITING_CLIENT_STATES.includes(deliveryState)) return { status: state, label: AWAITING_CLIENT_SIGNATURE_LABEL, variant: 'warning' }
  if (deliveryState === 'SIGNED') return { status: state, label: deliveryStateText(deliveryState).label, variant: 'success' }
  if (ENDED_UNSIGNED_STATES.includes(deliveryState)) {
    const text = deliveryStateText(deliveryState)
    return { status: state, label: `${plain.label} \u00b7 ${text.label}`, variant: text.tone === 'danger' ? 'destructive' : 'warning' }
  }
  return plain
}

/**
 * A stored money figure as the listing shows it: the server's decimal string with its currency and
 * the integer part grouped in thousands ("AED 9,450.00"). Text handling only, never arithmetic: the
 * digits are the server's (FR-67).
 */
export function moneyText(amount: string | null | undefined, currency: string | null | undefined): string | null {
  if (!amount) return null
  const [whole, fraction] = amount.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const text = fraction === undefined ? grouped : `${grouped}.${fraction}`
  return currency ? `${currency} ${text}` : text
}

export const PROPOSAL_SOURCE_LABEL: Record<string, string> = {
  MANUAL: 'Created here',
  PUSH: 'From Zoho',
}

export function proposalSourceLabel(source: string): string {
  return PROPOSAL_SOURCE_LABEL[source] ?? source
}

// ── Create form input rules (the server re-checks each one) ─────────────────────────────────────

/** A Zoho deal id: 18 or 19 digits after trimming (OQ-51: format only, no existence check). */
export const DEAL_REFERENCE_PATTERN = /^[0-9]{18,19}$/

export function isValidDealReference(value: string): boolean {
  return DEAL_REFERENCE_PATTERN.test(value.trim())
}

/** A syntactic address check only (`local@domain.tld`), at most 254 characters. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export function isValidClientEmail(value: string): boolean {
  const v = value.trim()
  return v.length > 0 && v.length <= 254 && EMAIL_PATTERN.test(v)
}

export const CLIENT_NAME_MAX = 200
export function isValidClientName(value: string): boolean {
  const v = value.trim()
  return v.length >= 1 && v.length <= CLIENT_NAME_MAX
}

/** Optional. Digits with the usual separators, 5 to 40 characters. */
const MOBILE_PATTERN = /^[0-9+()\-. ]{5,40}$/
export function isValidClientMobile(value: string): boolean {
  const v = value.trim()
  return v.length === 0 || (MOBILE_PATTERN.test(v) && /[0-9]/.test(v))
}

export type CreateField = 'dealReference' | 'clientDisplayName' | 'clientEmail' | 'clientMobile'

export interface CreateProposalValues {
  dealReference: string
  clientDisplayName: string
  clientEmail: string
  clientMobile: string
}

export const CREATE_FIELD_MESSAGE: Record<CreateField, string> = {
  dealReference: 'Enter the Zoho deal id: 18 or 19 digits.',
  clientDisplayName: `Enter the client name (up to ${CLIENT_NAME_MAX} characters).`,
  clientEmail: 'Enter a valid email address.',
  clientMobile: 'Enter a phone number with 5 to 40 digits and separators, or leave it empty.',
}

/** The fields that fail the form's own checks, in form order. Empty means ready to submit. */
export function createProposalErrors(values: CreateProposalValues): Partial<Record<CreateField, string>> {
  const errors: Partial<Record<CreateField, string>> = {}
  if (!isValidDealReference(values.dealReference)) errors.dealReference = CREATE_FIELD_MESSAGE.dealReference
  if (!isValidClientName(values.clientDisplayName)) errors.clientDisplayName = CREATE_FIELD_MESSAGE.clientDisplayName
  if (!isValidClientEmail(values.clientEmail)) errors.clientEmail = CREATE_FIELD_MESSAGE.clientEmail
  if (!isValidClientMobile(values.clientMobile)) errors.clientMobile = CREATE_FIELD_MESSAGE.clientMobile
  return errors
}

// ── Server errors by name ───────────────────────────────────────────────────────────────────────

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The `data.field` names crm-svc uses for CRM_PROPOSAL_INPUT_INVALID, mapped to the form's fields. */
const SERVER_FIELD_TO_FORM_FIELD: Record<string, CreateField> = {
  displayName: 'clientDisplayName',
  email: 'clientEmail',
  mobile: 'clientMobile',
  dealReference: 'dealReference',
}

export type CreateProposalFailure =
  | { kind: 'ALREADY_LIVE'; proposalId: string | null; message: string }
  | { kind: 'FIELD'; field: CreateField; message: string }
  | { kind: 'GENERAL'; message: string }

/**
 * What a failed `crmCreateProposal` means for the form. Reads only the error's NAME and its
 * `data.proposalId` / `data.field`, never `error.message`: graphql-request embeds the request
 * variables (the client's name, email and phone) in a ClientError's message.
 */
export function createProposalFailure(err: unknown): CreateProposalFailure {
  const code = gqlErrorCode(err)
  const data = gqlErrorData(err)
  switch (code) {
    case 'CRM_PROPOSAL_ALREADY_LIVE': {
      const id = typeof data?.proposalId === 'string' && UUID_PATTERN.test(data.proposalId) ? data.proposalId : null
      return { kind: 'ALREADY_LIVE', proposalId: id, message: 'A proposal for this deal is already in progress.' }
    }
    case 'CRM_PROPOSAL_REFERENCE_INVALID':
      return { kind: 'FIELD', field: 'dealReference', message: CREATE_FIELD_MESSAGE.dealReference }
    case 'CRM_PROPOSAL_INPUT_INVALID': {
      const field = typeof data?.field === 'string' ? SERVER_FIELD_TO_FORM_FIELD[data.field] : undefined
      if (field) return { kind: 'FIELD', field, message: CREATE_FIELD_MESSAGE[field] }
      return { kind: 'GENERAL', message: 'Some proposal details are not valid. Check the fields and try again.' }
    }
    default:
      return { kind: 'GENERAL', message: proposalErrorMessage(err, 'The proposal could not be created.') }
  }
}

/** Text for a failed proposal read or write, chosen by the server's error name. */
export function proposalErrorMessage(err: unknown, fallback: string): string {
  switch (gqlErrorCode(err)) {
    case 'CRM_NOT_ENTITLED':
      return 'Proposals are not enabled for your organisation.'
    case 'FORBIDDEN':
      return 'You do not have permission to do this.'
    case 'CRM_ACTOR_MISSING':
      return 'Your user is not set up for proposals yet. Ask an administrator.'
    case 'CRM_RECORD_NOT_FOUND':
      return 'This proposal was not found.'
    case 'CRM_PROPOSAL_INPUT_INVALID':
    case 'CRM_PROPOSAL_REFERENCE_INVALID':
      return 'The filter is not valid. Check the deal reference and try again.'
    default:
      return fallback
  }
}

/** True when the server said the record does not exist (also its answer for another tenant's id). */
export function isProposalNotFound(err: unknown): boolean {
  return gqlErrorCode(err) === 'CRM_RECORD_NOT_FOUND'
}

/** States in which the inputs can be edited (crm-svc: AWAITING_INPUT, INPUT_IN_PROGRESS). The server decides; this only hides a pointless link. */
export function isOpenForInput(state: string): boolean {
  return state === 'AWAITING_INPUT' || state === 'INPUT_IN_PROGRESS'
}
